import {SystemActions} from "../actions/SystemActions";
import {DeviceIdManager} from "../util/DeviceIdManager";
import RequestDto from "./messages/RequestDto";
import {AuthenticationError, performRawRestCall, performRawRestCallWithProgress} from "../util/HttpHelper";
import DBManager from "../db/DbManager";
import HandshakeDto from "./messages/HandshakeDto";
import RemoteOperationDto, {SendEmailRemoteOperationDto} from "./messages/RemoteOperationDto";
import {AppStore} from "../AppStore";
import {SyncState} from "../state/SyncState";
import {ConnectionStatus} from "../actions/SystemActionTypes";
import {EmailLocation} from '../locations/EmailLocation';
import {ComposeEmailActions} from '../actions/ComposeEmailActions';
import DbDraftEmail from '../domain/DbDraftEmail';
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {DecodedPgpKeyChangesDto, PgpKeyChangesDto} from "./messages/PgpKeyChangesDto";
import {SyncActions} from "../redux/SyncSlice";
import {decodePgpKeyDtos} from "../util/PGPUtil";
import {ItemChangeListDto} from "./messages/ItemChangeListDto";
import {OperationResponseDto} from "./messages/OperationResponseDto";
import {ItemActions} from "../actions/ItemActions";
import {ApiVersion} from "../APIVersion";
import {PgpKey} from "../domain/PgpKey";

const SYNC_DEBOUNCE_TIME = 200;

const deviceId = DeviceIdManager.getDeviceId();

const IgnoredSyncKeys = ["HasMoreChanges", "StartSyncId", "SyncId"];

export class SyncService {
    private store: AppStore;
    private db: DBManager;
    private outChangeKey: string = "";
    private currentServerId = 1;

    private started = false;
    private doneHandshake = false;

    /**
     * Subject used to prevent many sync changes being sent in very quick succession
     */
    private readonly operationsSubject: Subject<void> = new Subject<void>();

    private lastSyncState: SyncState | null = null;

    private inFlightOpIds: number[] = [];
    private completedIds = new Set<number>();

    private readonly retryCounts = new Map<number, number>();

    constructor(store: AppStore, db: DBManager) {
        this.store = store;
        this.db = db;

        this.operationsSubject
            .pipe(debounceTime(SYNC_DEBOUNCE_TIME))
            .subscribe({next: () => this.sendPendingOperationsSafe().catch(console.error)});

        store.subscribe(() => this.handleStateChanges().catch(console.error));
    }

    private async pollServer() {
        const {system} = this.store.getState();
        const username = system.userName;

        if (!username || system.serverApiVersion > ApiVersion) {
            // Don't allow syncing when the server version is ahead or we're not logged in
            setTimeout(() => this.pollServer(), 1000);
            return;
        }

        const dispatch = this.store.dispatch;
        try {
            if (this.doneHandshake) {
                const {sync} = this.store.getState();
                const url = `/Mail3/Poll/${this.currentServerId}/${sync.startSyncId}/${sync.endSyncId}?outChangeKey=${encodeURIComponent(this.outChangeKey)}&deviceId=${encodeURIComponent(deviceId)}`;
                const response = await performRawRestCallWithProgress<ItemChangeListDto>({
                    url,
                    method: "GET",
                    onOpened: () => dispatch(SystemActions.changeConnectionState(ConnectionStatus.Connected)),
                });

                const currentUserName = this.store.getState().system.userName;
                if (currentUserName === username) {
                    await this.handleResponseMessage(response);
                }
            } else {
                const handshake = await performRawRestCall<HandshakeDto>("/Mail3/Handshake", undefined, "GET");
                this.doneHandshake = true;
                this.currentServerId = handshake.ServerId ?? 1;

                const currentUserName = this.store.getState().system.userName;
                if (currentUserName === username) {
                    dispatch(SystemActions.performHandshake(handshake));
                }
            }
            setTimeout(() => this.pollServer(), 10);

        } catch (e) {
            if (e instanceof AuthenticationError) {
                dispatch(ItemActions.showItemContent({loginRequired: true}));
            }
            this.doneHandshake = false;
            dispatch(SystemActions.changeConnectionState(ConnectionStatus.Disconnected));
            dispatch(SystemActions.connectionError(e));
            setTimeout(() => this.pollServer(), 5000);
        }
    }

    private async handleStateChanges() {
        try {
            const {sync} = this.store.getState();
            if (sync === this.lastSyncState) {
                return;
            }

            this.lastSyncState = sync;

            this.operationsSubject.next();
        } catch (e) {
            console.error("Error in handleStateChanges: ", e);
        }
    }

    private async sendPendingOperationsSafe() {
        if (this.db.isInitialised()) {
            await this.sendPendingOperations();
        }
    }

    start() {
        if (!this.started) {
            this.started = true;
            setTimeout(() => this.pollServer(), 0);
        }
    }

    private async handleResponseMessage(items: ItemChangeListDto) {
        if (!items || !items.SyncId) {
            return;
        }

        if (items.Out) {
            this.outChangeKey = items.Out.ChangeKey;
        }

        // TODO: switch properly to the new server id!
        if (items.ServerId && items.ServerId !== this.currentServerId) {
            this.currentServerId = items.ServerId;
            items.HasMoreChanges = true;
        }

        console.log("Received sync Items response:", items.StartSyncId, "/", items.SyncId, "   ", Object.keys(items).filter(i => !IgnoredSyncKeys.includes(i)), ", HasMoreChanges:", items.HasMoreChanges);

        if (items.PublicKeys) {
            items.DecodedPublicKeys = await this.enrichPgpKeyItemChanges(items.PublicKeys, this.store.getState().accountsState.pgpKeys);
        }
        if (items.SecretKeys) {
            items.DecodedSecretKeys = await this.enrichPgpKeyItemChanges(items.SecretKeys, this.store.getState().accountsState.pgpKeys);
        }

        this.store.dispatch(SyncActions.syncResponseReceived(items));

        await this.db.saveSyncStateChange(items);

        if (items.Folders) {
            const {folderState} = this.store.getState();
            await this.db.saveFolders(folderState.folderList);
        }
    }

    private async enrichPgpKeyItemChanges(keyChanges: PgpKeyChangesDto, allKeys: PgpKey[]): Promise<DecodedPgpKeyChangesDto> {
        return {
            Changed: await decodePgpKeyDtos(keyChanges.Changed, allKeys),
            DeletedIds: keyChanges.DeletedIds,
        };
    }

    private async removeFinishedOperations(operationIds: number[], successful: boolean): Promise<void> {
        await this.db.removeOperations(operationIds);

        operationIds.forEach(id => this.retryCounts.delete(id));

        if (successful) {
            await this.performOperationCleanup(operationIds);
        }

        console.debug("In flight operations: ", this.inFlightOpIds);
        this.inFlightOpIds = this.inFlightOpIds.filter(id => !operationIds.includes(id));
        console.debug("In flight operations: ", this.inFlightOpIds);

        const {sync} = this.store.getState();
        const {operations} = sync;
        if (operations.length > 0) {
            this.operationsSubject.next();
        }
    }

    private async performOperationCleanup(completedOpIds: number[]): Promise<void> {
        const {sync} = this.store.getState();
        const {operations} = sync;

        let emailsChanged = false;
        for (const completedOpId of completedOpIds) {
            const operation = operations.find(i => i.Id === completedOpId);
            if (operation) {
                console.debug("Operation completed: ", operation);

                if (operation.SendEmail && !operation.SendEmail.Email.IsDraft) {
                    await this.db.deleteDraftEmails([operation.SendEmail.Email.Uid]);

                    console.debug("Draft email deleted: ", operation.SendEmail.Email.Uid);
                }

                if (operation.ManageEmails) {
                    const manageEmails = operation.ManageEmails;
                    emailsChanged = emailsChanged
                        || manageEmails?.Delete !== undefined
                        || manageEmails?.MarkAsJunk !== undefined
                        || manageEmails?.MoveToFolderId !== undefined;
                }
            } else {
                console.warn("Unable to find operation for cleanup", completedOpId);
                debugger;
            }
        }
    }

    private async handleFailedOperations(response: OperationResponseDto): Promise<void> {
        if (!response.FailedOps || response.FailedOps.length === 0) {
            return;
        }

        const failedOpIds = response.FailedOps.map(fo => fo.Id);

        if (failedOpIds.length > 0) {
            failedOpIds.forEach(id => this.retryCounts.delete(id));
            await this.removeFinishedOperations(failedOpIds, false);
        }

        let failureMessage = response.FailedOps.length === 1
            ? response.FailedOps[0].Error
            : "Some operations couldn't be synced with the remote server";

        const {operations} = this.store.getState().sync;
        for (const operation of operations) {
            const failedOp = response.FailedOps.find(fo => fo.Id === operation.Id);
            if (failedOp) {
                console.warn("Operation failed: ", failedOp, operation);

                if (operation.SendEmail) {
                    await this.restoreFailedSentEmail(operation.SendEmail);

                    failureMessage = `Your email couldn't be sent: ${failedOp.Error}`;
                }
                // TODO: make the error more specific for each type of operation (e.g. indicate that an email couldn't be sent if sending an email!)
            }
        }

        this.store.dispatch(SystemActions.showError(failureMessage));
    }

    private async restoreFailedSentEmail(sendDraftEmail: SendEmailRemoteOperationDto): Promise<void> {
        const draftEmail = await this.db.getDraftEmail(sendDraftEmail.Email.Uid);
        if (draftEmail) {
            const restoredDraftEmail: DbDraftEmail = {...draftEmail, IsNew: false, IsDraft: true};
            await this.db.saveDraftEmail(restoredDraftEmail);
            this.store.dispatch(ComposeEmailActions.amendDraftEmail(restoredDraftEmail));

            EmailLocation.openDraftEmailByUid(sendDraftEmail.Email.Uid);
        } else {
            this.store.dispatch(SystemActions.showError("Unable to restore email that failed to send"));
        }
    }

    private async sendFiles(fileOperations: RemoteOperationDto[]) {
        if (fileOperations.length === 0) {
            return;
        }

        // Remove the operations from the queued list
        const opIds = fileOperations.map(o => o.Id);

        const response: OperationResponseDto = {CompletedOpIds: opIds, FailedOps: undefined, ServerId: this.currentServerId};
        await this.handleOperationsResponseMessage(response);
    }

    private async sendPendingOperations() {
        try {
            const {sync, system} = this.store.getState();
            const {operations} = sync;

            if (system.isOffline) {
                if (operations.length > 0) {
                    console.debug("Offline, not performing operations");
                }
                return;
            }

            if (!system.userName || system.serverApiVersion > ApiVersion) {
                // Don't allow syncing when the server version is ahead or we're not logged in
                return;
            }

            const pendingOperations = operations.filter(op => !this.inFlightOpIds.includes(op.Id) && !this.completedIds.has(op.Id));

            this.inFlightOpIds = [...this.inFlightOpIds, ...pendingOperations.filter(o => !o.SendEmail).map(op => op.Id)];
            console.debug("In flight operations: ", this.inFlightOpIds);

            let operationsToSend = pendingOperations.filter(o => isSimpleOperation(o) && !o.SendEmail);
            if (operationsToSend.length === 0) {
                // Only try sending one email send at a time
                const sendEmailOps = pendingOperations.filter(isSimpleOperation);
                if (sendEmailOps.length > 0) {
                    operationsToSend = [sendEmailOps[0]];
                    this.inFlightOpIds = [...this.inFlightOpIds, sendEmailOps[0].Id];
                }
            }

            if (operationsToSend.length > 0) {
                const request: RequestDto = {
                    Ops: operationsToSend,
                    ServerId: this.currentServerId,
                };

                console.debug(`Sending ${operationsToSend.length} operations request`);

                try {
                    const response = await performRawRestCall<OperationResponseDto>(`/Mail3/PerformOperations`, request);
                    if (response.ServerId && response.ServerId !== request.ServerId) {
                        this.currentServerId = response.ServerId;
                        debugger;
                        this.inFlightOpIds = [];
                        await this.handleOperationsResponseMessage({ServerId: this.currentServerId, CompletedOpIds: undefined, FailedOps: undefined});
                        return;
                    }
                    // TODO: check the response status etc.!
                    await this.handleOperationsResponseMessage(response);
                } catch (e) {
                    console.error("Network error performing operations", e);
                    operationsToSend.filter(o => o.SendEmail).forEach(o => {
                        this.retryCounts.set(o.Id, (this.retryCounts.get(o.Id) ?? 0) + 1);
                    });
                    const failedOperations = operationsToSend.filter(o => (this.retryCounts.get(o.Id) ?? 0) > 1);
                    if (failedOperations.length > 0) {
                        const failedResponse = {
                            ServerId: request.ServerId,
                            CompletedOpIds: undefined,
                            FailedOps: failedOperations.map(o => ({Id: o.Id, Error: o.SendEmail ? "Network error - your attachments might be too large" : "Network error"})),
                        };
                        console.log("Failing operations: ", failedOperations);
                        await this.handleOperationsResponseMessage(failedResponse);
                    } else {
                        await this.handleOperationsResponseMessage({ServerId: this.currentServerId, CompletedOpIds: undefined, FailedOps: undefined});
                        console.debug("In flight operations: ", this.inFlightOpIds);
                        this.inFlightOpIds = [];
                        console.debug("In flight operations: ", this.inFlightOpIds);
                    }
                }
            }

            const fileOperations = pendingOperations.filter(op => !isSimpleOperation(op));

            // TODO: combine queued addition/removal of attachments prior to sending the attachments to the server!

            if (fileOperations.length > 0) {
                await this.sendFiles(fileOperations);
            }

        } catch (ex) {
            console.error("Perform operation error", ex);
            console.debug("In flight operations: ", this.inFlightOpIds);
            this.inFlightOpIds = [];
            console.debug("In flight operations: ", this.inFlightOpIds);
            // TODO: handle processing errors
        }
    }

    private async handleOperationsResponseMessage(response: OperationResponseDto) {
        if (response.CompletedOpIds) {
            response.CompletedOpIds.forEach(id => this.completedIds.add(id));
        }
        if (response.FailedOps) {
            response.FailedOps.forEach(op => this.completedIds.add(op.Id));
        }
        if (response.CompletedOpIds) {
            const completedOpIds = response.CompletedOpIds;
            await this.removeFinishedOperations(completedOpIds, true);
        }
        await this.handleFailedOperations(response);

        if (response.CompletedOpIds || response.FailedOps) {
            console.log("Operations response:", Object.keys(response).join(", "));

            if (response.CompletedOpIds && response.CompletedOpIds.length > 0) {
                console.debug("CompletedOpIds:", response.CompletedOpIds);
            }
            if (response.FailedOps && response.FailedOps.length > 0) {
                console.warn("FailedOps:", response.FailedOps);
            }
        }

        const operations = await this.db.getAllOperations();
        this.store.dispatch(SyncActions.setOperations({operations}));
    }
}

function isSimpleOperation(operation: RemoteOperationDto) {
    return !operation.AddAttachment && !operation.RemoveAttachment && !operation.RenameAttachment;
}
